import { Tooltip, Typography } from "@mui/material";
import type { GridApiPremium, GridColDef, GridDataSource, GridSortModel } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { GRID_DEFAULT_PAGE_SIZE } from "@entrilia/shared/components/grid/gridHelper";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import { convertISODate, convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import type { DataCollectionRequestInfo } from "../../../../api/types/dataCollectionTypes";
import ScheduleIcon from "@entrilia/shared/icons/ScheduleIcon";
import storage from "../../../../storage/storage";
import StepsProgress from "../../../common/StepsProgress";
import { useFilterContext } from "../../../common/filters/FilterContext";
import { sortAction } from "../../../common/filters/filterState";
import DataRequestStatusTag from "./DataRequestStatusTag";
import DataRequestsListActionsContext from "./DataRequestsListActionsContext";
import { getDataRequestTemplateCategoryName } from "./dataRequestHelper";
import { ActionsCell } from "./dataRequestsGridProvider";

const CategoryCell = ({ row }: { row: DataCollectionRequestInfo }) => {
  const { categories } = useDefinedContext(DataRequestsListActionsContext);
  return getDataRequestTemplateCategoryName(row, categories);
};

const columnDefinitions: GridColDef<DataCollectionRequestInfo>[] = [
  {
    field: "name",
    headerName: "Name",
    sortable: false,
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => <GridTextHoverLink to={row.id} text={row.name} />,
  },
  {
    field: "templateName",
    headerName: "Request Template",
    sortable: false,
    flex: 1,
    minWidth: 160,
  },
  {
    field: "reportingDate",
    headerName: "Reporting Date",
    flex: 0.5,
    minWidth: 144,
    valueFormatter: (value: string) => convertISODate(value),
  },
  {
    field: "status",
    headerName: "Status",
    sortable: false,
    flex: 0.5,
    minWidth: 160,
    renderCell: ({ row }) => <DataRequestStatusTag status={row.status} />,
  },
  {
    field: "auditory",
    headerName: "Recipient Type",
    sortable: false,
    flex: 1,
    minWidth: 160,
    valueFormatter: (value: string) => (value ? autoFormatCamelCase(value) : ""),
  },
  {
    field: "category",
    headerName: "Category",
    sortable: false,
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => <CategoryCell row={row} />,
  },
  {
    field: "publishedAt",
    headerName: "Published at",
    flex: 0.5,
    minWidth: 168,
    renderCell: ({ row }) => {
      if (row.status === "Scheduled" && row.scheduledToBePublishedAt) {
        return (
          <>
            <Tooltip
              title={`The request will be published on ${convertISODate(row.scheduledToBePublishedAt)}, at ${convertISODate(row.scheduledToBePublishedAt, "p")}`}
            >
              <ScheduleIcon color="primary" />
            </Tooltip>
            <Typography sx={{ ml: 1 }}>{convertISODateTime(row.scheduledToBePublishedAt)}</Typography>
          </>
        );
      }

      return convertISODateTime(row.publishedAt);
    },
  },
  {
    field: "submittedSubmissions",
    headerName: "Submission Progress",
    sortable: false,
    width: 160,
    renderCell: ({ row }) =>
      row.status === "Draft" || row.status === "Scheduled" || row.totalSubmissions === 0 ? null : (
        <StepsProgress completed={row.submittedSubmissions} total={row.totalSubmissions} />
      ),
  },
  {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    width: 72,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row }) => <ActionsCell row={row} />,
  },
];

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
  filterId: string;
}

const DataRequestsGrid = ({ apiRef, dataSource, filterId }: Props) => {
  const { filterState, setFilterState } = useFilterContext<DataCollectionRequestInfo>();

  const sortModel = useMemo(() => (filterState.sorting ? [filterState.sorting] : []), [filterState.sorting]);

  const [dataSourceError, setDataSourceError] = useState<Error>();

  const handleSortModelChange = useCallback(
    (gridSortModel: GridSortModel) => {
      setFilterState(sortAction(gridSortModel));
    },
    [setFilterState]
  );

  const handleDataSourceError = useCallback(
    (error: Error) => {
      logError(error, "[DataRequestsGrid]");
      setDataSourceError(error);
      storage.clearFilterState(filterId);
    },
    [filterId]
  );

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading data requests failed" />;
  }

  return (
    <DataGrid<DataCollectionRequestInfo>
      apiRef={apiRef}
      columns={columnDefinitions}
      lazyLoading
      dataSource={dataSource}
      paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
      onDataSourceError={handleDataSourceError}
      noRowsText="No data requests"
      disableColumnReorder
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
    />
  );
};

export default DataRequestsGrid;
